import { BellIcon, ChevronUpIcon } from '@orn/shared-web/icons';
import { cx } from '@orn/shared-web/utils/cx';

const PILL_CLASS = cx(
  'mx-auto flex h-7.5 w-fit max-w-full items-center gap-1 rounded-full px-2',
  // An inset ring rather than a border: Figma's inside stroke takes no room from the padding.
  'bg-primary/50 ring-(--color-bg-primary) shadow-[0_6px_30px_rgba(0,0,0,0.08)] ring-1 ring-inset'
);

const NoActiveAlertsPillContent = () => (
  <>
    <span className="flex min-w-0 items-center gap-1.5">
      <BellIcon aria-hidden="true" className="text-fg-tertiary size-4 shrink-0" />
      <span className="text-tertiary truncate text-sm font-medium">Alerts</span>
    </span>
    <ChevronUpIcon aria-hidden="true" className="text-fg-quaternary size-4.5 shrink-0" />
  </>
);

interface INoActiveAlertsPillProps {
  /** The pill was clicked. Absent on the decorative copy drawn over a moving card. */
  onOpen?: () => void;
  className?: string;
}

/**
 * The phone composer's way into the Notification Center when no alert is
 * active (Figma "No-active-alerts pill", `9155:237753`): a neutral capsule
 * reading "Alerts" that opens the full center. Without `onOpen` it is an inert,
 * `aria-hidden` copy for the overlay a presentation change draws.
 */
const NoActiveAlertsPill = ({ onOpen, className }: INoActiveAlertsPillProps) =>
  onOpen ? (
    <button
      type="button"
      aria-expanded="false"
      aria-label="Open alerts"
      onClick={onOpen}
      className={cx(
        PILL_CLASS,
        'outline-focus-ring cursor-pointer focus-visible:outline-2 focus-visible:outline-offset-2',
        className
      )}
    >
      <NoActiveAlertsPillContent />
    </button>
  ) : (
    <div aria-hidden="true" className={cx(PILL_CLASS, 'pointer-events-none', className)}>
      <NoActiveAlertsPillContent />
    </div>
  );

export default NoActiveAlertsPill;
